<template>
  <VaDropdown :offset="[13, 0]" class="notification-dropdown" stick-to-edges :close-on-content-click="false">
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary">
        <template v-if="newNotificationsCount > 0">
          <VaBadge overlap>
            <template #text>
              {{ Math.min(newNotificationsCount, 99) }}{{ newNotificationsCount > 99 ? '+' : '' }}</template
            >
            <VaIconNotification class="notification-dropdown__icon" />
          </VaBadge>
        </template>
        <template v-else>
          <VaIconNotification class="notification-dropdown__icon" />
        </template>
      </VaButton>
    </template>
    <VaDropdownContent class="h-full md:max-w-[530px] sm:max-w-[420px] sm:h-auto">
      <section class="sm:max-h-[320px] p-4 overflow-auto">
        <VaList class="space-y-1 mb-2">
          <template v-for="(item, index) in notificationsWithRelativeTime" :key="item.id">
            <VaListItem class="text-base" @click="redirect(item)">
              <!-- Circulo pequeño que indica notificacion sin revisar -->
              <VaListItemSection v-if="item.estado === NotificationStatus.PENDING" icon class="mx-0 p-0">
                <div class="h-3 w-3 bg-primary rounded-full"></div>
              </VaListItemSection>
              <!-- Crear mapeo de iconos
              <VaListItemSection icon class="mx-0 p-0">
                <VaIcon :name="item.icon" color="secondary" />
              </VaListItemSection>
              -->
              <VaListItemSection class="text-sm">
                {{ item.mensaje }}
              </VaListItemSection>
              <VaListItemSection icon class="mx-1">
                {{ item.fecha_creacion }}
              </VaListItemSection>
            </VaListItem>
            <VaListSeparator class="mx-3" />
          </template>
        </VaList>

        <VaButton
          v-if="notificationsWithRelativeTime.length > 0"
          preset="primary"
          class="w-full"
          @click="displayAllNotifications = !displayAllNotifications"
          >{{ displayAllNotifications ? 'Menos notificaciones' : 'Ver todas las notificaciones' }}
        </VaButton>
        <div v-else class="text-center text-gray-500">No hay notificaciones pendientes.</div>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import VaIconNotification from '../../../icons/VaIconNotification.vue'
import dataStorageManager, { BucketDefaultIDs } from '../../../../managers/data/DataStorageManager'
import {
  INotification,
  NotificationStatus,
  NotificationType,
  NotificationsPageMap,
} from '../../../../services/api-services/NotificationsServices'
import { IUser, UserRoles } from '../../../../services/api-services/UsersServices'
import { IArmador } from '../../../../services/api-services/ArmadoresServices'
import { IProveedor } from '../../../../services/api-services/ProveedoresServices'
import { IRequest, RequestObjectType, RequestStatus } from '../../../../services/api-services/RequestsServices'
import identityManager, { LoggedInUser } from '../../../../managers/identity/IdentityManager'
import { useRouter } from 'vue-router'

const router = useRouter()

const { t, locale } = useI18n()
const baseNumberOfVisibleNotifications = 4
const rtf = new Intl.RelativeTimeFormat(locale.value, { style: 'short' })
const displayAllNotifications = ref(false)

const loggedInUser: Ref<LoggedInUser | null> = ref(null)
const userData: Ref<IUser | null> = ref(null)
const armadorData: Ref<IArmador | null> = ref(null)
const proveedorData: Ref<IProveedor | null> = ref(null)

const usuarios: Ref<IUser[]> = ref([])
const armadores: Ref<IArmador[]> = ref([])
const proveedores: Ref<IProveedor[]> = ref([])
const solicitudes: Ref<IRequest[]> = ref([])

const notifications: Ref<INotification[]> = ref([])
dataStorageManager.subscribeBucket<INotification>(BucketDefaultIDs.NOTIFICATIONS, (notifs) => {
  notifications.value = notifs
})
// IDs de armadores que el representante actual representa (rol=6)
const representedArmadorIds = computed<number[]>(() => {
  if (!userData.value || userData.value.rol !== UserRoles.REPRESENTANTE) return []
  return solicitudes.value
    .filter(
      (r) =>
        r.tipo_objeto === RequestObjectType.RepresentanteArmador &&
        r.id_objeto_2 === userData.value!.id_usuario.toString() &&
        r.estado === RequestStatus.APPROVED &&
        new Date(r.fecha_caducidad) > new Date(),
    )
    .map((r) => parseInt(r.id_objeto))
})

const showNotifications: Ref<INotification[]> = computed(() => {
  // Pendientes y de hoy (+10 horas -10 horas)
  const prevLimit = new Date()
  prevLimit.setHours(prevLimit.getHours() - 10)
  const nextLimit = new Date()
  nextLimit.setHours(nextLimit.getHours() + 10)
  return notifications.value
    .filter(
      (notification) =>
        notification.estado === NotificationStatus.PENDING ||
        (new Date(notification.fecha_creacion) >= prevLimit && new Date(notification.fecha_creacion) <= nextLimit),
    )
    .filter((n) => {
      if (!loggedInUser.value || !userData.value) {
        return false
      }
      if (n.tipo === NotificationType.GENERAL) {
        let urole = userData.value.rol
        if (urole === UserRoles.SUPERVISOR) {
          urole = UserRoles.ADMIN
        }
        return n.receptor
          .toString()
          .split(',')
          .map((r) => parseInt(r))
          .includes(urole)
      } else {
        if (n.receptor.toString().includes('A')) {
          // Armador, se quita la A y se compara con id_armador
          const receptorId = parseInt(n.receptor.replace('A', ''))
          if (armadorData.value && armadorData.value.id === receptorId) {
            return true
          }
          // Representante: verificar si representa a ese armador
          if (userData.value.rol === UserRoles.REPRESENTANTE && representedArmadorIds.value.includes(receptorId)) {
            return true
          }
        } else if (n.receptor.toString().includes('P')) {
          // Proveedor, se quita la P y se compara con id_proveedor
          if (proveedorData.value) {
            const receptorId = parseInt(n.receptor.replace('P', ''))
            if (proveedorData.value.id === receptorId) {
              return true
            }
          }
        } else {
          // Si viene sin nada se asume armador
          const receptorId = parseInt(n.receptor)
          if (armadorData.value && armadorData.value.id === receptorId) {
            return true
          }
          // Representante: verificar si representa a ese armador
          if (userData.value.rol === UserRoles.REPRESENTANTE && representedArmadorIds.value.includes(receptorId)) {
            return true
          }
        }
        return false
      }
    })
})
const newNotificationsCount = computed(
  () => showNotifications.value.filter((n) => n.estado === NotificationStatus.PENDING).length,
)

/*const notifications: INotification[] = [
  {
    message: '4 pending requests',
    icon: 'favorite_outline',
    id: 1,
    separator: true,
    updateTimestamp: makeDateFromNow(-3 * 60 * 1000),
  },
  {
    message: '3 new reports',
    icon: 'calendar_today',
    id: 2,
    separator: true,
    updateTimestamp: makeDateFromNow(-12 * 60 * 60 * 1000),
  },
  {
    message: 'Whoops! Your trial period has expired.',
    icon: 'error_outline',
    id: 3,
    separator: true,
    updateTimestamp: makeDateFromNow(-2 * 24 * 60 * 60 * 1000),
  },
  {
    message: 'It looks like your timezone is set incorrectly, please change it to avoid issues with Memory.',
    icon: 'schedule',
    id: 4,
    updateTimestamp: makeDateFromNow(-2 * 7 * 24 * 60 * 60 * 1000),
  },
  {
    message: '2 new team members added',
    icon: 'group_add',
    id: 5,
    separator: false,
    updateTimestamp: makeDateFromNow(-3 * 60 * 1000),
  },
  {
    message: 'Monthly budget exceeded by 10%',
    icon: 'trending_up',
    id: 6,
    separator: true,
    updateTimestamp: makeDateFromNow(-3 * 24 * 60 * 60 * 1000),
  },
  {
    message: '7 tasks are approaching their deadlines',
    icon: 'alarm',
    id: 7,
    separator: false,
    updateTimestamp: makeDateFromNow(-5 * 60 * 60 * 1000),
  },
  {
    message: 'New software update available',
    icon: 'system_update',
    id: 8,
    separator: true,
    updateTimestamp: makeDateFromNow(-1 * 24 * 60 * 60 * 1000),
  },
] //.sort((a, b) => new Date(b.updateTimestamp).getTime() - new Date(a.updateTimestamp).getTime())*/

const TIME_NAMES = {
  second: 1000,
  minute: 1000 * 60,
  hour: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
  week: 1000 * 60 * 60 * 24 * 7,
  month: 1000 * 60 * 60 * 24 * 30,
  year: 1000 * 60 * 60 * 24 * 365,
}

const getTimeName = (differenceTime: number) => {
  return Object.keys(TIME_NAMES).reduce(
    (acc, key) => (TIME_NAMES[key as keyof typeof TIME_NAMES] < differenceTime ? key : acc),
    'month',
  ) as keyof typeof TIME_NAMES
}

const notificationsWithRelativeTime = computed(() => {
  const sortedNotifications = showNotifications.value.sort(
    (a, b) => new Date(b.fecha_creacion).getTime() - new Date(a.fecha_creacion).getTime(),
  )
  const list = displayAllNotifications.value
    ? sortedNotifications
    : sortedNotifications.slice(0, baseNumberOfVisibleNotifications)

  return list.map((item, index) => {
    const timeDifference = Math.round(new Date().getTime() - new Date(item.fecha_creacion).getTime())
    const timeName = getTimeName(timeDifference)

    let separator = false

    const nextItem = list[index + 1]
    if (nextItem) {
      const nextItemDifference = Math.round(new Date().getTime() - new Date(nextItem.fecha_creacion).getTime())
      const nextItemTimeName = getTimeName(nextItemDifference)

      if (timeName !== nextItemTimeName) {
        separator = true
      }
    }

    return {
      ...item,
      fecha_creacion: rtf.format(-1 * Math.round(timeDifference / TIME_NAMES[timeName]), timeName),
      separator,
    }
  })
})

onMounted(() => {
  dataStorageManager.subscribeBucket<IUser>(BucketDefaultIDs.USERS, (data) => {
    usuarios.value = data
    loadUserData()
  })
  dataStorageManager.subscribeBucket<IArmador>(BucketDefaultIDs.ARMADORES, (data) => {
    armadores.value = data
    loadUserData()
  })
  dataStorageManager.subscribeBucket<IProveedor>(BucketDefaultIDs.PROVEEDORES, (data) => {
    proveedores.value = data
    loadUserData()
  })
  dataStorageManager.subscribeBucket<IRequest>(BucketDefaultIDs.SOLICITUDES, (data) => {
    solicitudes.value = data
  })
})

function loadUserData() {
  if (!userData.value || (userData.value.rol == UserRoles.EXTERNO && !armadorData.value && !proveedorData.value)) {
    const user = identityManager.getUser()
    loggedInUser.value = user
    if (user) {
      userData.value = usuarios.value.find((u) => u.id_usuario === user.id_usuario) || null
      if (userData.value) {
        if (userData.value.rol == UserRoles.EXTERNO) {
          const armadorFinded = armadores.value.find((a) => a.rut_representante === userData.value!.rut)
          if (armadorFinded) {
            armadorData.value = armadorFinded
          } else {
            const proveedorFinded = proveedores.value.find((p) => p.rut_representante === userData.value!.rut)
            if (proveedorFinded) {
              proveedorData.value = proveedorFinded
            }
          }
        }
      }
    }
  }
}

function redirect(notification: INotification) {
  notification.estado = NotificationStatus.READED
  dataStorageManager.performOperation(BucketDefaultIDs.NOTIFICATIONS, 'updateCambio', notification)

  const page = NotificationsPageMap(notification)
  if (page) {
    //window.location.href = page
    router.push({ name: page })
  }
}
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }
}
</style>
